import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiService } from '../services/useApiService';
import LoadingSpinner from '../components/LoadingSpinner';
import NaibLogo from '../components/NaibLogo';
import { UserIcon, BriefcaseIcon, PhoneIcon, KeyIcon } from '@heroicons/react/24/outline';

function AcceptInvitation() {
  const { token } = useParams();
  const { users } = useApiService();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isNewUser, setIsNewUser] = useState(true);
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    position: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const validateInvitation = async () => {
      try {
        // For development testing with query params
        if (process.env.NODE_ENV === 'development' && !token) {
          const params = new URLSearchParams(window.location.search);
          const testError = params.get('error');
          const testExisting = params.get('existing');
          
          if (testError) {
            setError(testError);
          }
          if (testExisting === 'true') {
            setIsNewUser(false);
          }
          setLoading(false);
          return;
        }

        const response = await users.validateInvitation(token);
        setIsNewUser(response.data.isNewUser);
        if (!response.data.isNewUser) {
          await users.acceptInvitation(token);
          navigate('/login', { 
            state: { message: 'You have been added to the application. Please login to continue.' }
          });
        }
      } catch (error) {
        const apiError = error.response?.data?.error;
        if (apiError === 'Invalid or expired invitation') {
          setError(
            'This invitation link is invalid or has expired. Ask your administrator to resend the invitation from Settings → Users.'
          );
        } else {
          setError(apiError || 'Invalid invitation');
        }
      } finally {
        setLoading(false);
      }
    };

    validateInvitation();
  }, [token, navigate, users]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setError('Please accept the terms and conditions to continue');
      return;
    }
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      if (process.env.NODE_ENV === 'development' && !token) {
        navigate('/login', { 
          state: { message: 'Test account setup complete. Please login to continue.' }
        });
        return;
      }

      await users.acceptInvitation(token, formData);
      navigate('/login', { 
        state: { message: 'Account setup complete. Please login to continue.' }
      });
    } catch (error) {
      const apiError = error.response?.data?.error;
      if (apiError === 'Invalid or expired invitation') {
        setError(
          'This invitation link is invalid or has expired. Ask your administrator to resend the invitation from Settings → Users.'
        );
      } else {
        setError(apiError || 'Failed to accept invitation');
      }
    }
  };

  const TermsModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Terms and Conditions</h3>
          <div className="mt-2 px-7 py-3 text-left">
            <p className="text-sm text-gray-500">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam cursus tellus in quam tincidunt, 
              vitae efficitur elit blandit. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices 
              posuere cubilia curae; Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>
          </div>
          <div className="items-center px-4 py-3">
            <button
              onClick={() => setShowTerms(false)}
              className="px-4 py-2 bg-gray-900 text-white text-base font-medium rounded-lg w-full shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md text-center p-8 bg-white rounded-lg shadow-xl">
        <p className="text-red-600">{error}</p>
        <p className="mt-4 text-sm text-gray-500">
          If you already have an account, try signing in. Otherwise contact your organization administrator.
        </p>
      </div>
    </div>
  );

  if (!isNewUser) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-gray-600 text-center p-8 bg-white rounded-lg shadow-xl">
        Redirecting to login...
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-6 sm:px-6 lg:px-6">
      <div className="max-w-md w-full p-8 md:p-10 2xl:p-12 3xl:p-14 bg-white rounded-2xl shadow-xl">
        <div className="pb-4">
          <NaibLogo logoClassName="h-9 w-9" nameClassName="h-7 w-auto max-w-[180px]" alt="Onahiri" />
        </div>
        
        <div className="text-sm font-light text-gray-500 pb-8">
          Complete your account setup
        </div>

        <form className="flex flex-col" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4 mb-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="pb-2">
            <label htmlFor="fullName" className="block mb-2 text-sm font-medium text-gray-900">
              Full Name
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <UserIcon className="h-5 w-5" />
              </span>
              <input
                id="fullName"
                name="fullName"
                type="text"
                required
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              />
            </div>
          </div>

          <div className="pb-2">
            <label htmlFor="position" className="block mb-2 text-sm font-medium text-gray-900">
              Position
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <BriefcaseIcon className="h-5 w-5" />
              </span>
              <input
                id="position"
                name="position"
                type="text"
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="Your position"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              />
            </div>
          </div>

          <div className="pb-2">
            <label htmlFor="phoneNumber" className="block mb-2 text-sm font-medium text-gray-900">
              Phone Number
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <PhoneIcon className="h-5 w-5" />
              </span>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="+1 (555) 000-0000"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              />
            </div>
          </div>

          <div className="pb-2">
            <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900">
              Password
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <KeyIcon className="h-5 w-5" />
              </span>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="••••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
          </div>

          <div className="pb-6">
            <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-900">
              Confirm Password
            </label>
            <div className="relative text-gray-400">
              <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                <KeyIcon className="h-5 w-5" />
              </span>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 sm:text-sm rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                placeholder="••••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center mb-6">
            <input
              id="terms"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="w-4 h-4 text-gray-900 bg-gray-100 border-gray-300 rounded focus:ring-gray-500"
            />
            <label htmlFor="terms" className="ml-2 text-sm font-medium text-gray-500">
              I accept the{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="text-gray-900 hover:underline"
              >
                Terms and Conditions
              </button>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !acceptedTerms}
            className={`w-full text-white bg-gray-900 hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors duration-200 ${
              (loading || !acceptedTerms) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Complete Setup
          </button>
        </form>

        {showTerms && <TermsModal />}
      </div>
    </div>
  );
}

export default AcceptInvitation; 